import type { ReactNode } from 'react';
import { dataTableStyles } from '@/shared/ui/data-table/styles';

/**
 * The bar that replaces the toolbar while rows are selected.
 *
 * Rendered as a named region so it is reachable by landmark navigation, and
 * announced as a count in words rather than as a bare number — "3 selected"
 * reads as a sentence, "3" does not.
 *
 * Returns nothing when nothing is selected: an empty bar with a live region
 * around it is noise on every page that has no selection.
 */
export function BulkBar({
  selectedCount,
  onClearSelection,
  children,
}: {
  selectedCount: number;
  onClearSelection: () => void;
  children?: ReactNode;
}) {
  if (selectedCount === 0) return null;

  return (
    <div role="region" aria-label="Bulk actions" className={dataTableStyles.bulkBar}>
      <span className={dataTableStyles.bulkCount}>
        {selectedCount} {selectedCount === 1 ? 'row' : 'rows'} selected
      </span>
      <div className="ml-auto flex items-center gap-2">
        {children}
        <button
          type="button"
          className={dataTableStyles.pageButton}
          aria-label="Clear selection"
          onClick={onClearSelection}
        >
          Clear selection
        </button>
      </div>
    </div>
  );
}